import type { MemberProfile } from './activityApi'
import type { AvatarHud } from '../components/MemberAvatar'
import type { CodeSummaries } from './codeSummariesApi'

export type MemberIdentity = Pick<MemberProfile, 'user_id' | 'display_name' | 'handle' | 'bio' | 'avatar_url' | 'avatar_hud'>
export type MemberSummary = MemberIdentity & { accessible_dive_count?: number; latest_dive_at?: string | null; /** Shares one of my own divers (a second login, a test account). */ linked?: boolean }
export type ProfileDive = { id: number; dive_code: string; height: string; dive_datetime: string; visibility: 'public' | 'private'; poster_url: string | null; playback_url?: string | null; performance?: string | null; phase1_performance?: string | null; phase2_performance?: string | null }
export type DivePage = { items: ProfileDive[]; next_before: string | null }

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`/api/v1/users${path}`, { credentials: 'same-origin', ...options })
  if (!response.ok) {
    const error = new Error(`Request failed: ${response.status}`) as Error & { status: number }
    error.status = response.status
    throw error
  }
  return response.status === 204 ? undefined as T : response.json()
}

export function searchMembers(q: string, after?: number, signal?: AbortSignal): Promise<{ items: MemberSummary[]; next_after: number | null }> {
  const params = new URLSearchParams({ q })
  if (after) params.set('after', String(after))
  return request(`?${params}`, { signal })
}

/** Complete Community counts, computed server-side over the whole directory,
 * so an explicit continuation never silently truncates the section headers. */
export type MemberTotals = { members: number; divers: number; others: number; recent: number }
export type MemberDirectoryPage = {
  items: MemberSummary[]
  next_after: number | null
  /** Older servers answer without the flag: read as "unknown", never as false-complete. */
  has_more?: boolean
  totals?: MemberTotals
}

/** One bounded directory page (keyset on active account ids), replacing the
 * former up-to-20-request sequential load (discovery 2026-11-01, profil 6). */
export function getMemberDirectory(after?: number, signal?: AbortSignal): Promise<MemberDirectoryPage> {
  const params = new URLSearchParams()
  if (after) params.set('after', String(after))
  return request(`/directory?${params}`, { signal })
}
export function getProfileDives(id: number, before?: string): Promise<DivePage> {
  return request(`/${id}/dives${before ? `?before=${encodeURIComponent(before)}` : ''}`)
}
export function saveProfile(data: { display_name: string; handle: string | null; bio: string }): Promise<MemberIdentity> {
  return request('/me/profile', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
}
export function uploadAvatar(file: File): Promise<MemberIdentity> {
  return request('/me/avatar', { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file })
}
export function removeAvatar(): Promise<void> {
  return request('/me/avatar', { method: 'DELETE' })
}

export function saveAvatarStyle(avatar_hud: AvatarHud): Promise<MemberIdentity> {
  return request('/me/avatar-style', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ avatar_hud }) })
}

export type LeaderboardPeriod = 'week' | 'month'
export type LeaderboardMember = MemberIdentity & {
  attempts: number; sessions: number; codes: number
  median: number | null; delta: number | null; streak_weeks: number; last_attempt_at: string | null
}
export type Leaderboard = { period: LeaderboardPeriod; start: string; end: string; members: LeaderboardMember[] }
export function getLeaderboard(period: LeaderboardPeriod): Promise<Leaderboard> {
  return request(`/leaderboard?period=${period}`)
}

/** A member's per-code summaries over the dives visible to me (same shape as the Library's). */
export function getMemberCodes(id: number): Promise<CodeSummaries & { totals: { attempt_count: number; session_count: number; first_attempt_at: string | null; last_attempt_at: string | null } }> {
  return request(`/${id}/codes`)
}
